import { AlertTriangle, CheckCircle2 } from "lucide-react";

import type { ICampaignComparisonResponse } from "src/@types/campaign-comparison";

interface ComparisonBannersProps {
  error: unknown;
  result: ICampaignComparisonResponse | null;
}

function errorMessage(error: unknown): string {
  if ((error as { status?: number })?.status === 404)
    return "A campanha ou o resultado da comparação não foi encontrado.";
  if (typeof error === "string") return error;
  return (
    (error as { data?: { message?: string } })?.data?.message ||
    "Erro inesperado ao consultar as lojas. Tente novamente."
  );
}

export function ComparisonBanners({ error, result }: ComparisonBannersProps) {
  return (
    <>
      {error && (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 p-4 dark:border-red-900/50 dark:bg-red-950/30"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 text-red-700 dark:text-red-300" />
          <div>
            <div className="font-semibold text-red-800 dark:text-red-200">
              Não foi possível concluir a comparação
            </div>
            <div className="text-sm text-red-700 dark:text-red-300">
              {errorMessage(error)}
            </div>
          </div>
        </div>
      )}

      {result && !result.hasPropagation && (
        <div
          role="status"
          className="flex items-start gap-3 rounded-lg border border-blue-300 bg-blue-50 p-4 dark:border-blue-900/50 dark:bg-blue-950/30"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 text-blue-600 dark:text-blue-400" />
          <div>
            <div className="font-semibold text-blue-800 dark:text-blue-200">
              Sem propagação registrada no portal
            </div>
            <div className="text-sm text-blue-700 dark:text-blue-300">
              Esta campanha não foi propagada para lojas pelo portal (ou a
              propagação não foi registrada). Mesmo assim, comparamos diretamente
              as lojas que possuem a campanha no VAR — a coluna de propagação não
              se aplica aqui.
            </div>
          </div>
        </div>
      )}

      {result &&
        result.hasPropagation &&
        (result.summary.allSyncedInPage ? (
          <div
            role="status"
            className="flex items-center gap-3 rounded-lg border border-green-300 bg-green-50 p-4 dark:border-green-900/50 dark:bg-green-950/30"
          >
            <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
            <div>
              <div className="font-semibold text-green-800 dark:text-green-200">
                Nenhuma discrepância nesta página
              </div>
              <div className="text-sm text-green-700 dark:text-green-300">
                As {result.summary.pageStores} loja(s) desta página estão
                sincronizadas com a retaguarda.
              </div>
            </div>
          </div>
        ) : (
          <div
            role="alert"
            className="flex items-center gap-3 rounded-lg border border-yellow-300 bg-yellow-50 p-4 dark:border-yellow-900/50 dark:bg-yellow-950/30"
          >
            <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            <div>
              <div className="font-semibold text-yellow-800 dark:text-yellow-200">
                Discrepâncias encontradas nesta página
              </div>
              <div className="text-sm text-yellow-700 dark:text-yellow-300">
                {result.summary.divergent > 0 &&
                  `${result.summary.divergent} loja(s) com dados divergentes. `}
                {result.summary.notFoundInStore > 0 &&
                  `${result.summary.notFoundInStore} loja(s) sem a campanha. `}
                {result.summary.unreachable > 0 &&
                  `${result.summary.unreachable} loja(s) inacessível(is). `}
                Clique em "Detalhe" para ver o conflito.
              </div>
            </div>
          </div>
        ))}
    </>
  );
}
